/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { Label } from '@msdyn365-commerce-modules/order-management';
import {
    // getSalesOrderHistoryWithHydrationsAction,
    // GetSalesOrderHistoryWithHydrationsInput,
    IOrderHistory
    // IPaging
} from './actions/get-order-shipment-history';
import { getSalesOrder, ISalesOrder } from './components/get-sales-order';
// import { OrderHistoryFilterComponent, OrderHistoryFilterState } from './components/lds-order-history-filter';
import { OrderHistoryTableComponent } from './components/table-view';
import { ILdsOrderHistoryData } from './lds-order-history.data';
import { ILdsOrderHistoryProps } from './lds-order-history.props.autogenerated';
// import { getOrderShipmentsHistoryAsync } from '../../actions/DataActionExtension.g';
import { getUnitOrderHistoryWithHydrationsAction } from './actions/get-unit-order-shipment-history';

/**
 * Specifies different layouts for order history.
 */
export enum OrderHistoryViewMode {
    listView = 0,
    detailedView = 1
}

export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
    orderCountLabel?: React.ReactNode;
    extraActions?: React.ReactNode;
}

export interface IList {
    listProps: INodeProps;
    salesOrders: ISalesOrder[];
}

export interface IOrderHistoryViewState {
    isDataReady: boolean;
    hasError: boolean;
    isLoading: boolean;
    isShowMoreButton: boolean;
    connectionFailed?: boolean;
    unitId: any;
}

export interface IOrderHistoryViewProps extends ILdsOrderHistoryProps<ILdsOrderHistoryData> {
    orderHistory: IOrderHistory;
    orderHistoryProps: IModuleProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    header: IHeader;
    backToShoppingLink?: React.ReactNode;
    list?: IList;
    table?: React.ReactNode;
    moreButton?: React.ReactNode;
    viewState: IOrderHistoryViewState;
    getUnitOrderHistory: any;
}

/**
 *
 * OrderHistory component.
 * @extends {React.Component<ILdsOrderHistoryProps<ILdsOrderHistoryData>>}
 */
@observer
class OrderHistory extends React.Component<ILdsOrderHistoryProps<ILdsOrderHistoryData>> {
    @observable private orderHistory!: IOrderHistory;

    @observable private viewState!: IOrderHistoryViewState;

    @observable private _viewMode: OrderHistoryViewMode;

    // private filterState: OrderHistoryFilterState;

    // private pageSize!: number;

    // private paging!: IPaging;

    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    get isDataReady(): boolean {
        return (this.props.data.paymentTypes && this.props.data.paymentTypes.result && this.props.data.paymentTypes.status) === 'SUCCESS';
    }

    constructor(props: ILdsOrderHistoryProps<ILdsOrderHistoryData>) {
        super(props);
        this.initState();
        this._viewMode = OrderHistoryViewMode.detailedView;
        // this.filterState = OrderHistoryFilterState.CurrentUser;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
    }

    public componentDidMount(): void {
        // if (this.props.context.actionContext.requestContext.user.isAuthenticated && this.props.data.paymentTypes.result) {
        //     console.log('TRUE', this.props.data.paymentTypes);
        //     // this._updateOrderHistory(this.props.data.paymentTypes);
        // } else {
        // this.viewState.isDataReady = true;
        // }
    }

    public shouldComponentUpdate(nextProps: ILdsOrderHistoryProps<ILdsOrderHistoryData>, nextState: IOrderHistoryViewState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }

        return true;
    }

    public render(): JSX.Element | null {
        if (!this.isDataReady) {
            return null;
        }
        const {
            config: { heading, className, showChannelInfo },
            resources: {
                genericErrorMessage,
                noOrderHistoryLable,
                moreButtonText,
                backToShopping,
                orderCountLabel,
                ordersCountLabel
                // loadingLabel
            }
        } = this.props;
        const { salesOrders, products } = this.orderHistory;
        const { hasError, isShowMoreButton, isLoading } = this.viewState;
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const channelIdentities = this.props.data.channels.result && this.props.data.channels.result.channelIdentities;
        const orgUnitLocations = this.props.data.orgUnitLocations.result && this.props.data.orgUnitLocations.result.orgUnitLocations;

        let viewProps: IOrderHistoryViewProps = {
            ...this.props,
            getUnitOrderHistory: this.getUnitOrderHistory,
            orderHistory: this.orderHistory,
            viewState: this.viewState,
            orderHistoryProps: {
                moduleProps: this.props,
                className: classname('ms-order-history', className)
            },
            header: {
                headerProps: { className: 'ms-order-history__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        {...heading}
                        className='ms-order-history__heading'
                        text={heading.text}
                        tag={heading.tag || 'h2'}
                        editProps={{ onEdit: this.handleOrderHistoryHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-history__btn-keep-shopping' href={backToShoppingUrl}>
                    {backToShopping}
                </Button>
            )
        };

        if (hasError) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-history__alert' label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!salesOrders || salesOrders.length === 0) {
            viewProps = {
                ...viewProps,
                emptyMessage: <p className='ms-order-history__empty-message'>{noOrderHistoryLable}</p>
            };

            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const _orderCountLabel = salesOrders.length === 1 ? orderCountLabel : ordersCountLabel;
        this.payLoad.contentAction.etext = TelemetryConstant.ViewMore;
        const viewMoreAttributes = getTelemetryAttributes(this.telemetryContent!, this.payLoad);

        const productsDetailedViewLimit = 5;

        viewProps = {
            ...viewProps,
            getUnitOrderHistory: this.getUnitOrderHistory,
            header: {
                ...viewProps.header,
                orderCountLabel: <span className='ms-order-history__order-count'>{`(${salesOrders.length} ${_orderCountLabel})`}</span>
            },
            table:
                this._viewMode === OrderHistoryViewMode.listView ? (
                    <OrderHistoryTableComponent
                        id={this.props.id}
                        typeName={this.props.typeName}
                        context={this.props.context}
                        className='ms-order-history__table-list'
                        shouldShowChannelInfo={this.props.config.showChannelInfo ?? false}
                        data={{
                            orderHistory: this.orderHistory,
                            channelIdentities,
                            orgUnitLocations
                        }}
                        resources={this.props.resources}
                    />
                ) : (
                    undefined
                ),
            list:
                this._viewMode === OrderHistoryViewMode.detailedView
                    ? {
                          listProps: { className: 'ms-order-history__sales-order-list' },
                          salesOrders: salesOrders.map(salesOrder =>
                              getSalesOrder({
                                  ...this.props,
                                  products,
                                  salesOrder,
                                  channelIdentities,
                                  orgUnitLocations,
                                  showChannelInfo,
                                  salesLinesLimit: productsDetailedViewLimit,
                                  telemetryContent: this.telemetryContent,
                                  unitId: this.viewState.unitId
                              })
                          )
                      }
                    : undefined,
            moreButton: isShowMoreButton && (
                <Button
                    className={`ms-order-history__btn-more ${isLoading ? 'is-busy' : ''}`}
                    {...viewMoreAttributes}
                    disabled={isLoading}
                    onClick={this.onMoreButtonClick}
                >
                    {moreButtonText}
                </Button>
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleOrderHistoryHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);

    private readonly onMoreButtonClick = () => {
        void this.getUnitOrderHistory(this.props.data.paymentTypes, 0);
    };

    private readonly initState = () => {
        this.orderHistory = {
            salesOrders: [],
            products: [],
            nextPageToken: ''
        };
        this.viewState = {
            isDataReady: false,
            hasError: false,
            isShowMoreButton: (this.props.config.pageSize || 0) > 0,
            isLoading: false,
            connectionFailed: false,
            unitId: ''
        };
    };

    private readonly getUnitOrderHistory = async (wardId: any, skip: number, shouldShowMore?: any, unitList?: any): Promise<boolean> => {
        const actionContext = this.props.context.actionContext;

        const input = {
            AccountNum: wardId,
            skip: skip
        };
        this.viewState.unitId = unitList ? unitList[0].UNITNUMBERNAME : '';

        if (skip === 0) {
            this.viewState.hasError = false;
        }

        const response = await getUnitOrderHistoryWithHydrationsAction(input, actionContext).catch(() => {
            this.viewState.connectionFailed = true;
        });

        if (response) {
            this.viewState.connectionFailed = false;
            this.viewState.hasError = false;
            if (response.salesOrders.length < 5 && shouldShowMore) {
                shouldShowMore(false);
            } else if (response.salesOrders.length === 5 && shouldShowMore) {
                shouldShowMore(true);
            }

            // console.log(`data for id: ${wardId} with skip: ${skip} `, response);
            if (skip > 0) {
                const { products, salesOrders } = response;
                this.orderHistory.products.push(...products);
                this.orderHistory.salesOrders.push(...salesOrders);
            } else {
                const { products, salesOrders } = response;
                this.orderHistory = { salesOrders: [], products: [], nextPageToken: '' };
                this.orderHistory.products.push(...products);
                this.orderHistory.salesOrders.push(...salesOrders);
            }
        } else if (skip === 0) {
            // Avoid showing another unit's orders when this unit's request fails; surface generic error in render().
            this.orderHistory = { salesOrders: [], products: [], nextPageToken: '' };
            this.viewState.hasError = true;
        }
        this.viewState.isLoading = false;

        await Promise.resolve();
        return !!response;
    };
}

export default OrderHistory;
